import { z } from 'zod';
/**
 * MITRE ATT&CK as a Wazuh rule carries it (P11.8.6): technique ids, their names
 * in the same order, and the tactics they serve — `553`, a file deleted, is
 * `T1070.004` and `T1485`, File Deletion and Data Destruction, serving Defense
 * Evasion and Impact.
 * A rule's own, never the file's, so not attacker text; bounded all the same, and
 * an alert whose tags do not fit is kept without them.
 */
export const MITRE_LIMITS = { entries: 10, text: 64 };
export const MitreTechniqueId = z.string().regex(/^T\d{4}(\.\d{3})?$/);
const MitreText = z
    .string()
    .min(1)
    .max(MITRE_LIMITS.text)
    .regex(/^[\w ,'()/&.-]+$/);
export const WazuhMitre = z.object({
    id: z.array(MitreTechniqueId).max(MITRE_LIMITS.entries),
    tactic: z.array(MitreText).max(MITRE_LIMITS.entries),
    technique: z.array(MitreText).max(MITRE_LIMITS.entries),
});
/** An event's MITRE ATT&CK tags, for the console (P11.8.6). */
export const MitreTags = z.object({
    tactics: z.array(MitreText).max(MITRE_LIMITS.entries),
    techniques: z
        .array(z.object({ id: MitreTechniqueId, name: MitreText.nullable() }))
        .max(MITRE_LIMITS.entries),
});
/** A tactic as the Events filter takes it: one of a rule's, typed or picked. */
export const MitreTactic = MitreText;
/**
 * ATT&CK's Enterprise tactics, as Wazuh's rules name them: suggestions for the
 * Events filter, never a validation rule (a newer ruleset may name another).
 */
export const MITRE_TACTICS = [
    'Reconnaissance',
    'Resource Development',
    'Initial Access',
    'Execution',
    'Persistence',
    'Privilege Escalation',
    'Defense Evasion',
    'Credential Access',
    'Discovery',
    'Lateral Movement',
    'Collection',
    'Command and Control',
    'Exfiltration',
    'Impact',
];
/** ATT&CK's ids for those tactics, as SCA policies give them (`TA0005`). */
export const MITRE_TACTIC_IDS = {
    TA0043: 'Reconnaissance',
    TA0042: 'Resource Development',
    TA0001: 'Initial Access',
    TA0002: 'Execution',
    TA0003: 'Persistence',
    TA0004: 'Privilege Escalation',
    TA0005: 'Defense Evasion',
    TA0006: 'Credential Access',
    TA0007: 'Discovery',
    TA0008: 'Lateral Movement',
    TA0009: 'Collection',
    TA0011: 'Command and Control',
    TA0010: 'Exfiltration',
    TA0040: 'Impact',
};
/**
 * The tags in an event's `raw` (`rule.mitre`, as `rawFor` writes it), or null.
 * Read back from storage, so parsed again rather than trusted.
 */
export function mitreOf(raw) {
    if (!isRecord(raw) || !isRecord(raw['rule']))
        return null;
    const parsed = WazuhMitre.safeParse(raw['rule']['mitre']);
    if (!parsed.success)
        return null;
    const { id, tactic, technique } = parsed.data;
    if (id.length === 0 && tactic.length === 0)
        return null;
    return {
        tactics: [...new Set(tactic)],
        techniques: id.map((each, i) => ({ id: each, name: technique[i] ?? null })),
    };
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
//# sourceMappingURL=mitre.js.map